import { FC } from 'react';
import { useDesktop } from '@utils/hooks/useDesktop';
import { usePaulina } from './PaulinaProvider';
import { usePaulinaVersionControls } from './usePaulinaVersionControls';
import { PaulinaPill } from './PaulinaPill';
import { PaulinaPanel } from './PaulinaPanel';
import { PaulinaSheet } from './PaulinaSheet';
import { PaulinaNudgeBubble } from './PaulinaNudgeBubble';

/**
 * AgentChatWidget — host de la entrada de Paulina (Opción A). Ya NO renderiza el FAB
 * flotante ni la tarjeta-nudge flotante: orquesta las superficies por VIEWPORT.
 *
 *   DESKTOP → PaulinaPill (entrada nombrada, z-80, oculta si hay pedido) +
 *             PaulinaPanel (chat abierto, z-95, cede ante el toast de pedido) +
 *             PaulinaNudgeBubble (proactividad: speech-bubble, suprimida si hay pedido).
 *   MÓVIL   → PaulinaSheet (bottom-sheet, z-95). La ENTRADA en móvil es el avatar de
 *             Paulina en MobileTopBarV4 (header), que abre este sheet vía el contexto.
 *
 * ROLLOUT (feature agent-chat) — reparto:
 *   - INVITACIÓN ("Conoce a Paulina") y el PROMPT de feedback al desactivar viven a nivel Layout
 *     (PaulinaInviteHost / PaulinaRolloutOverlays), FUERA del PaulinaGate: en control el provider
 *     no se monta (invitación) y el revert lo desmonta (prompt). Este widget solo aparece cuando
 *     Paulina YA está montada (offered/flag OFF).
 *   - DESACTIVAR: el control "Desactivar Paulina" del header (overflow) lo cablea este host vía
 *     usePaulinaVersionControls (canDeactivate = root efectivo + can_revert). Al confirmar revierte
 *     el rollout (Paulina deja de montarse) y dispara el prompt de motivos.
 *
 * Toda la lógica del chat (polling, historial, nudges, telemetría pau_*, navegación, aislamiento
 * multi-tenant) vive en `usePaulinaChat` (compartida vía PaulinaProvider); la cesión ante pedidos
 * (`hayPedido`) viene de `usePaulinaShouldYield`. Lo operativo SIEMPRE gana: el toast de pedido
 * (z-99) queda por encima de toda la UI de Paulina (pill z-80, sheet/panel z-95).
 */

export const AgentChatWidget: FC = () => {
  const isDesktop = useDesktop();
  const paulina = usePaulina();
  const { canDeactivate, deactivate } = usePaulinaVersionControls();

  if (!paulina || !paulina.enabled) return null;

  const {
    open,
    setOpen,
    turns,
    input,
    setInput,
    busy,
    error,
    nudge,
    sesionChatId,
    bottomRef,
    toggleOpen,
    goTo,
    aceptarNudge,
    descartarNudge,
    clearConversation,
    send,
    doSend,
    hayPedido,
  } = paulina;

  // Solo ofrecemos "Desactivar Paulina" si el backend autoriza revertir y el rol es root efectivo.
  const onDeactivate = canDeactivate ? deactivate : undefined;

  // MÓVIL: la entrada es el avatar del header (MobileTopBarV4); aquí solo el sheet.
  if (!isDesktop) {
    if (!open) return null;
    return (
      <PaulinaSheet
        turns={turns}
        input={input}
        setInput={setInput}
        busy={busy}
        error={error}
        bottomRef={bottomRef}
        goTo={goTo}
        send={send}
        doSend={doSend}
        setOpen={setOpen}
        clearConversation={clearConversation}
        sesionChatId={sesionChatId}
        nudge={nudge}
        aceptarNudge={aceptarNudge}
        descartarNudge={descartarNudge}
        onDeactivate={onDeactivate}
      />
    );
  }

  // DESKTOP: pill (entrada) + panel (chat) + nudge (proactividad).
  return (
    <>
      {open ? (
        <PaulinaPanel
          turns={turns}
          input={input}
          setInput={setInput}
          busy={busy}
          error={error}
          bottomRef={bottomRef}
          goTo={goTo}
          send={send}
          doSend={doSend}
          setOpen={setOpen}
          clearConversation={clearConversation}
          sesionChatId={sesionChatId}
          hayPedido={hayPedido}
          onDeactivate={onDeactivate}
        />
      ) : (
        // Lo operativo gana: la pill se oculta por completo cuando hay pedido entrante.
        !hayPedido && <PaulinaPill hasNudge={Boolean(nudge)} onOpen={toggleOpen} />
      )}

      {/* Proactividad: speech-bubble sobre la pill, suprimida si hay pedido o chat abierto. */}
      {!open && !hayPedido && nudge && (
        <PaulinaNudgeBubble nudge={nudge} onAccept={aceptarNudge} onDismiss={descartarNudge} />
      )}
    </>
  );
};
